/**
 * SaveHintTooltip Component
 *
 * One-time hint tooltip that appears above the heart icon
 * on property cards to educate new users about saving properties.
 * Dismisses after first save or when user clicks dismiss.
 */

import { useState, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const HINT_DISMISSED_KEY = 'safaqat24.favorites.hintDismissed';

export interface SaveHintTooltipProps {
  children: ReactNode;
  onDismiss?: () => void;
}

export default function SaveHintTooltip({
  children,
  onDismiss,
}: SaveHintTooltipProps) {
  const { t, i18n } = useTranslation('favorites');
  const isRTL = i18n.language === 'ar';
  const [isVisible, setIsVisible] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(HINT_DISMISSED_KEY);
      if (!dismissed) {
        // Show tooltip after a short delay for better UX
        const timer = setTimeout(() => {
          setIsVisible(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const handleDismiss = () => {
    try {
      localStorage.setItem(HINT_DISMISSED_KEY, 'true');
    } catch {
      // Ignore localStorage errors
    }
    setIsVisible(false);
    onDismiss?.();
  };

  // Public method to dismiss from parent (e.g., after first save)
  const dismiss = () => {
    handleDismiss();
  };

  // Expose dismiss method via data attribute for parent access
  useEffect(() => {
    // Store dismiss function globally for parent to call
    (window as typeof window & { __dismissSaveHint?: () => void }).__dismissSaveHint = dismiss;
    return () => {
      delete (window as typeof window & { __dismissSaveHint?: () => void }).__dismissSaveHint;
    };
  }, []);

  return (
    <div className="relative inline-block">
      {children}

      {/* Tooltip */}
      {isVisible && (
        <div
          className={`absolute z-50 bottom-full mb-2 ${
            isRTL ? 'right-1/2 translate-x-1/2' : 'left-1/2 -translate-x-1/2'
          }`}
        >
          {/* Tooltip Content */}
          <div className="relative bg-[var(--pv-color-primary)] text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap animate-fadeIn">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium">
                {t('favorites.hint.tapToSave')}
              </span>
              <button
                onClick={handleDismiss}
                className="p-0.5 hover:bg-white/20 rounded transition-colors"
                aria-label={t('favorites.hint.dismiss')}
              >
                <i className="ri-close-line text-sm" />
              </button>
            </div>

            {/* Arrow pointing down to heart icon */}
            <div
              className={`absolute top-full ${
                isRTL ? 'right-1/2 translate-x-1/2' : 'left-1/2 -translate-x-1/2'
              } w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[var(--pv-color-primary)]`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Export utility to dismiss hint programmatically
export function dismissSaveHint() {
  try {
    localStorage.setItem(HINT_DISMISSED_KEY, 'true');
    const windowWithHint = window as typeof window & { __dismissSaveHint?: () => void };
    if (windowWithHint.__dismissSaveHint) {
      windowWithHint.__dismissSaveHint();
    }
  } catch {
    // Ignore errors
  }
}
